import { useRef, useState, useContext, useMemo, useEffect } from 'react'
import { useReactToPrint } from 'react-to-print'
import { useTranslation } from 'react-i18next'
import { isEmpty, debounce } from 'lodash'
import { useSelector } from 'react-redux'
import moment from 'moment'

import {
	Button,
	Paper,
	Dialog,
	DialogContent,
	DialogActions,
	DialogTitle,
	FormControl,
	InputLabel,
	Select,
	MenuItem,
	FormGroup,
	FormControlLabel,
	Checkbox,
	Alert,
	FormLabel,
	RadioGroup,
	Radio,
} from '@mui/material'
import { DataGridPremium, useGridApiRef } from '@mui/x-data-grid-premium'
import { LoadingButton } from '@mui/lab'
import { makeStyles } from '@mui/styles'

import { datesValues, nonDatesValues, operatorOptions } from 'utils/functions/conditionsOptions'
import { getAllColumnsByView } from './CreatViewComponents/ViewDialogUtils'
import { IconThemeContext } from '../../../custom-components/context/IconThemesContext'
import { useQuery } from '@tanstack/react-query'

const useStyles = makeStyles(() => ({
	root: {
		'& .MuiButton-root': {
			textTransform: 'none',
		},
	},
	printContainer: {
		display: 'none',
		'@media print': {
			display: 'block',
		},
	},
}))

const operatorMap = {
	EQ: '=',
	NE: '!=',
}

const PAGE_TO_PRINT_TYPE = {
	CURRENT_PAGE: 'currentPage',
	ALL_PAGES: 'allPages',
}

const ALLERT_MESSAGE = `All pages - Please note that if the data set is too large, this could exceed the time or memory limit. 
					In this case, please check the option above to only export the records in the current page. 
					Then repeat the process for the next page.`

const DEFAULT_PAGE_TO_PRINT = PAGE_TO_PRINT_TYPE.ALL_PAGES

const PrintDataGrid = ({
	printTitle,
	gridColumns,
	rows,
	viewData,
	aggregationModel,
	rowGroupingModel,
	sortModel,
	filterModel,
	getVisibleRows,
	hasMoreRecords,
	dataLoading,
	onHandledLoadAllRecords,
	onHandledCancelLoadingRecords,
}) => {
	const { t } = useTranslation()
	const { environment } = useSelector((state) => state)

	const { iconTheme } = useContext(IconThemeContext)
	const classes = useStyles(iconTheme)

	const {
		data: allColumns,
		isLoading,
		isFetching,
	} = useQuery({
		queryKey: ['print', 'getAllColumnsByView', viewData?.type],
		queryFn: () =>
			getAllColumnsByView({
				viewDataType: viewData?.type,
				apiToken: environment?.apiToken,
				formKey: viewData?.formKey,
			}),
		enabled: !!viewData?.type,
	})
	const printRef = useRef()
	const gridApiRef = useGridApiRef()
	const [openDialog, setOpenDialog] = useState(false)

	// print settings
	const [orientation, setOrientation] = useState('portrait')
	const [paperSize, setPaperSize] = useState('A4')
	const [showHeader, setShowHeader] = useState(true)
	const [pageToPrint, setPageToPrint] = useState(DEFAULT_PAGE_TO_PRINT)

	const [error, setError] = useState(null)

	const pageToPrintOptions = useMemo(() => {
		return [
			{
				value: PAGE_TO_PRINT_TYPE.CURRENT_PAGE,
				label: 'Current page',
			},
			{
				value: PAGE_TO_PRINT_TYPE.ALL_PAGES,
				label: 'All pages',
			},
		]
	}, [t])

	const userOffset = useMemo(() => {
		return environment.userCurrent.time.timezone.offset || 0
	}, [environment])

	const operators = useMemo(() => {
		return operatorOptions(t)
	}, [t])

	const dateValues = useMemo(() => {
		return datesValues(t)
	}, [t])

	function buildDateTimeValueByColumnType(value, columnType) {
		switch (columnType) {
			case 'datetime':
				return moment.utc(value).utcOffset(userOffset).format('MM/DD/YYYY HH:mm:ss')
			case 'date':
				return moment.utc(value).utcOffset(userOffset).format('MM/DD/YYYY')
			case 'time':
				return moment.utc(value).utcOffset(userOffset).format('HH:mm:ss')
			default:
				return value
		}
	}

	const nonDateValues = useMemo(() => {
		return nonDatesValues(t)
	}, [t])

	const queryString = useMemo(() => {
		if (isEmpty(viewData)) return ''

		const conditions = viewData?.queries[0]?.filter.conditions || []
		const query = conditions.map((condition) => {
			const { target, preset, type, values } = condition
			let column =
				allColumns?.find((column) => column.name === target) ||
				gridColumns?.find((gridColumn) => gridColumn.field === target)
			let field = column?.title || column?.headerName
			const columnType = column?.type?.toLowerCase()
			if (!field) {
				field = target
			}
			let ope = operatorMap[type]
			if (!ope) {
				ope = operators.find((item2) => item2.type === type)?.name
			}
			if (!ope) {
				ope = type
			}
			let val = ''
			if (preset) {
				val = dateValues?.find((item2) => item2.preset === preset)?.name
				if (!val) {
					val = nonDateValues?.find((item2) => item2.preset === preset)?.name
				}
				if (!val) {
					val = preset
				}
			} else if (values) {
				if (Array.isArray(values) && values.length > 1) {
					val =
						' [ ' +
						values
							?.map((value) => {
								return buildDateTimeValueByColumnType(value, columnType)
							})
							.join(' ; ') +
						' ]'
				} else {
					val = buildDateTimeValueByColumnType(values[0], columnType)
				}
			}
			return field + ' ' + ope + ' ' + val
		})

		if (query && query.length > 0) {
			let tmpFilter = query.filter((x) => x !== '')
			return tmpFilter?.join(', ')
		}

		if (!query) {
			return ' '
		}
	}, [viewData, gridColumns, allColumns])

	const [showTable, setShowTable] = useState(false)
	const [isPrinting, setIsPrinting] = useState(false)
	const [loadingPrint, setLoadingPrint] = useState(false)

	const promiseResolveRef = useRef(null)
	const [gridHeight, setGridHeight] = useState(undefined)

	const memoGridColumns = useMemo(() => {
		return gridColumns || []
	}, [gridColumns])

	const memoGridRows = useMemo(() => {
		if (pageToPrint === PAGE_TO_PRINT_TYPE.CURRENT_PAGE) {
			const parentVisibleRows = getVisibleRows?.() || rows || []
			return parentVisibleRows
		}
		console.log('rows', rows.length)
		return rows || []
	}, [pageToPrint, rows])

	const [visibleRows, setVisibleRows] = useState([])
	const [isChunking, setIsChunking] = useState(false)
	const [isReady, setIsReady] = useState(false)

	const [loadingAllPages, setLoadingAllPages] = useState(false)

	useEffect(() => {
		if (!loadingAllPages) return
		const handler = debounce((loading) => {
			if (!loading) {
				setShowTable(true)
			}
		}, 1000)

		handler(dataLoading)

		// Cleanup to cancel pending debounce when component unmounts or dataLoading changes
		return () => {
			handler.cancel()
		}
	}, [dataLoading, loadingAllPages])

	useEffect(() => {
		if (!showTable) return
		let index = 0
		const chunkSize = 500
		let isCancelled = false
		const result = []

		function renderChunk() {
			if (isCancelled) return

			const chunk = memoGridRows.slice(index, index + chunkSize)
			result.push(...chunk)
			index += chunkSize

			if (index < memoGridRows.length) {
				setTimeout(renderChunk, 0)
			} else {
				if (!isCancelled) {
					setVisibleRows(result)
					setIsChunking(false)
					setTimeout(() => setIsReady(true), 0)
				}
			}
		}

		// Reset states before starting
		setVisibleRows([])
		setIsChunking(true)
		setIsReady(false)

		renderChunk()

		return () => {
			isCancelled = true
			setVisibleRows([])
			setIsReady(false)
			setIsChunking(false)
		}
	}, [showTable, memoGridRows])

	const adjustDataGridHeightForPrint = () => {
		const childElement = printRef?.current?.querySelector('.MuiDataGrid-root')
		if (!childElement) return
		const childHeight = childElement?.offsetHeight
		if (childHeight !== gridHeight) {
			setGridHeight(childHeight + 52)
		}

		const style = document.createElement('style')

		style.media = 'print'
		style.innerHTML = `
			@page {
				size: ${paperSize} ${orientation}; 
			}

			.MuiDataGrid-root{
				overflow: visible !important;
				max-height: none !important;		
				height: ${childElement.offsetHeight + 52}px !important
			}

			.MuiDataGrid-main,
			.MuiDataGrid-virtualScroller {
				overflow: visible !important;
				height: auto !important;
			}

			.MuiDataGrid-actionsCell {
				.MuiButtonBase-root
				{
					display: none !important;
				}
			}
		`
		document.head.appendChild(style)
	}

	useEffect(() => {
		const timeout = setTimeout(() => {
			if (isReady && isPrinting && promiseResolveRef.current) {
				adjustDataGridHeightForPrint()
				requestAnimationFrame(() => {
					if (promiseResolveRef.current) {
						promiseResolveRef.current()
					}
				})
			}
		}, 300)

		return () => clearTimeout(timeout)
	}, [isReady, isPrinting])

	useEffect(() => {
		if (!showTable) return
		setLoadingAllPages(false)
		let intervalId
		function checkApiRef() {
			if (gridApiRef.current) {
				clearInterval(intervalId)
				handlePrint()
			}
		}

		intervalId = setInterval(checkApiRef, 2000) // Check every 100ms

		return () => clearInterval(intervalId)
	}, [showTable])

	const pageStyle = `
			@page {
				size: ${paperSize} ${orientation}; 
			}
		`

	const title = useMemo(() => {
		return printTitle || viewData?.name
	}, [printTitle, viewData])

	const handlePrint = useReactToPrint({
		contentRef: printRef,
		documentTitle: title,
		pageStyle,
		onBeforePrint: () => {
			gridApiRef.current?.resize()
			return new Promise((resolve) => {
				setTimeout(() => {
					promiseResolveRef.current = resolve
					setIsPrinting(true)
				}, 5000)
			})
		},
		onAfterPrint: () => {
			handleCancel()
		},
	})

	async function handleOk() {
		setLoadingPrint(true)
		// Let the UI show the spinner before triggering expensive rendering
		if (pageToPrint === PAGE_TO_PRINT_TYPE.ALL_PAGES && hasMoreRecords) {
			try {
				// Wait for all records to be loaded
				setLoadingAllPages(true)
				await onHandledLoadAllRecords()
			} catch (e) {
				console.error('Failed to load all records:', e)
				setLoadingAllPages(false)
				setLoadingPrint(false)
				setError('Failed to load all records for printing.')
				return
			}
		} else {
			// Let the UI show the spinner before triggering expensive rendering
			setTimeout(() => {
				setShowTable(true)
			}, 100)
		}
	}

	function handleCancel() {
		promiseResolveRef.current = null
		setShowTable(false)
		setIsReady(false)
		setIsPrinting(false)
		setLoadingAllPages(false)
		setLoadingPrint(false)
		setVisibleRows([])
		setPageToPrint(DEFAULT_PAGE_TO_PRINT)
		setOpenDialog(false)

		if (dataLoading) {
			onHandledCancelLoadingRecords?.()
		}
	}

	return (
		<div>
			<MenuItem onClick={() => setOpenDialog(true)}>PDF</MenuItem>
			<Dialog
				sx={{
					'& .MuiDialog-paper': {
						width: '35% !important',
						maxWidth: '100% !important',
					},
				}}
				open={openDialog}
				onClose={() => setOpenDialog(false)}
			>
				<DialogTitle>Print {printTitle || viewData?.name}</DialogTitle>
				<DialogContent>
					<p>Select your print options:</p>
					<FormControl fullWidth margin="dense">
						<InputLabel id="orientation-select-small-label">Orientation</InputLabel>
						<Select
							size="small"
							labelId="orientation-select-small-label"
							id="orientation-select-small"
							label={'Orientation'}
							value={orientation}
							onChange={(e) => setOrientation(e.target.value)}
						>
							<MenuItem value="portrait">Portrait</MenuItem>
							<MenuItem value="landscape">Landscape</MenuItem>
						</Select>
					</FormControl>
					<FormGroup style={{ marginLeft: '20px' }}>
						<FormControlLabel
							control={
								<Checkbox checked={showHeader} onChange={(e) => setShowHeader(e.target.checked)} />
							}
							label="Show header"
						/>
					</FormGroup>

					<FormControl>
						<FormLabel id="page-to-print-radio-buttons-group-label">Page to print</FormLabel>
						<RadioGroup
							style={{ marginLeft: '20px' }}
							aria-labelledby="page-to-print-radio-buttons-group-label"
							name="page-to-print-radio-buttons-group"
							value={pageToPrint}
							onChange={(e) => setPageToPrint(e.target.value)}
							row
						>
							{pageToPrintOptions.map((option) => (
								<FormControlLabel
									key={option.value}
									value={option.value}
									control={<Radio size="small" />}
									label={option.label}
								/>
							))}
						</RadioGroup>
					</FormControl>
					<Alert severity="warning">{ALLERT_MESSAGE}</Alert>
				</DialogContent>
				<DialogActions>
					<Button onClick={handleCancel} className={classes.icon}>
						{t('common:misc.cancel')}
					</Button>
					<LoadingButton
						className={classes.icon}
						style={{
							minWidth: '80px',
						}}
						loading={isChunking || loadingPrint}
						disabled={isEmpty(viewData) ? false : isLoading || isFetching}
						loadingPosition="start"
						onClick={handleOk}
					>
						{t('common:misc.exportPrint')}
					</LoadingButton>
				</DialogActions>
			</Dialog>

			<div className={classes.printContainer}>
				<Paper
					ref={printRef}
					style={{
						overflow: 'visible',
						display: 'block',
						width: '100%',
						height: 'auto',
						maxHeight: '999999px',
					}}
				>
					{showHeader && (
						<div style={{ borderBottom: '2px solid black', marginBottom: '10px' }}>
							<h2>{title}</h2>
							<div>{queryString}</div>
							<div>{`${environment.userCurrent.firstName} ${environment.userCurrent.lastName} - ${environment.userCurrent.email}`}</div>
							<div>
								{moment
									.utc()
									.utcOffset(userOffset || 0)
									.format('MM/DD/YYYY HH:mm:ss')}
							</div>
						</div>
					)}

					{showTable && (
						<div className={'gridContainer'}>
							<DataGridPremium
								sx={{
									'& .MuiDataGrid-footerContainer': {
										display: 'none !important',
									},
								}}
								loading={loadingPrint}
								apiRef={gridApiRef}
								rows={visibleRows}
								columns={memoGridColumns}
								aggregationModel={aggregationModel}
								rowGroupingModel={rowGroupingModel}
								sortModel={sortModel}
								filterModel={filterModel}
								disableVirtualization={true}
								defaultGroupingExpansionDepth={1}
								getRowHeight={() => {
									return 'auto'
								}}
							/>
						</div>
					)}
				</Paper>
			</div>
		</div>
	)
}

export default PrintDataGrid
